// ============================================================================
// Motore Report Operativi — Aesthetic OS
// ----------------------------------------------------------------------------
// Generazione SIMULATA (MOCK). Nessuna chiamata reale a ChatGPT.
// Produce un oggetto STRUTTURATO (intro, KPI, analisi, next steps) che la UI
// renderizza come documento premium nativo della piattaforma.
// Architettura pronta per: sostituire `generateOperationalReport` con una vera
// chiamata LLM (backend) e per caricare Prompt Template versionati.
// ============================================================================
import { clientMeta, clientFunnel } from "@/data/mockData";
import { eur, num } from "@/lib/format";

export const REPORT_MODEL = "gpt-5.4-mini";

const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export const monthLabel = (date) => `${MONTHS_IT[date.getMonth()]} ${date.getFullYear()}`;
export const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
export const addMonths = (date, n) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
};

const deltaStr = (curr, prev) => {
  if (!prev) return "n/d";
  const v = ((curr - prev) / prev) * 100;
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
};

// Template del prompt AI di base — NON viene mai sovrascritto.
// In futuro caricato da uno store di Prompt Template versionati.
export const PROMPT_TEMPLATE = {
  id: "operational-report-v1",
  model: REPORT_MODEL,
  system:
    "Sei un analista marketing senior di un'agenzia. Scrivi un report operativo mensile interno, " +
    "in italiano, professionale e sintetico, combinando le metriche Meta Ads con le Operational Funnel " +
    "Metrics provenienti dal Google Sheet del cliente. Evidenzia trend, criticità e raccomandazioni.",
};

export const buildPrompt = ({ clientName, period, meta, funnel, comparison, notes }) => {
  const notesBlock = notes && notes.trim()
    ? notes.trim()
    : "Nessuna modifica di campagna fornita dall'operatore per questo periodo.";
  return [
    `[SYSTEM]`,
    PROMPT_TEMPLATE.system,
    ``,
    `[CONTESTO]`,
    `Cliente: ${clientName}`,
    `Periodo analizzato: ${period}`,
    ``,
    `[METRICHE META ADS]`,
    `Spesa: ${eur(meta.spesa)} | Lead: ${num(meta.leads)} | CPL: ${eur(meta.cpl)} | Impression: ${num(meta.impression)} | Reach: ${num(meta.reach)}`,
    ``,
    `[OPERATIONAL FUNNEL METRICS (Google Sheet)]`,
    `Appuntamenti fissati: ${num(funnel.appointmentsBooked)} | Programmi venduti: ${num(funnel.programsSold)} | No Show: ${num(funnel.noShow)} | Numeri errati: ${num(funnel.wrongNumbers)} | Fuori zona: ${num(funnel.outsideServiceArea)} | Non interessati: ${num(funnel.notInterested)} | Contatti gestiti: ${num(funnel.managedContacts)} | Contatti da gestire: ${num(funnel.contactsToManage)}`,
    ``,
    `[CONFRONTO MESI PRECEDENTI]`,
    `Lead: ${deltaStr(meta.leads, comparison.prev1.leads)} vs mese precedente, ${deltaStr(meta.leads, comparison.prev2.leads)} vs due mesi fa`,
    `CPL: ${deltaStr(meta.cpl, comparison.prev1.cpl)} vs mese precedente`,
    ``,
    `[NOTE OPERATORE]`,
    notesBlock,
    ``,
    `[ISTRUZIONI]`,
    `Genera un report operativo mensile in italiano con: introduzione, tabella KPI con confronto mese su mese, analisi in paragrafi e una lista di prossimi passi.`,
  ].join("\n");
};

const simulatePrev = (meta, factor) => ({
  leads: Math.round(meta.leads * factor),
  cpl: +(meta.cpl * (2 - factor)).toFixed(2),
  spesa: +(meta.spesa * factor).toFixed(2),
});

// Anteprima del prompt AI costruito automaticamente (editabile solo per la
// singola generazione: il template base non viene mai sovrascritto).
export const previewPrompt = ({ client, days, period, notes }) => {
  const meta = clientMeta(client, days);
  const funnel = clientFunnel(client, days);
  const comparison = { prev1: simulatePrev(meta, 0.88), prev2: simulatePrev(meta, 0.79) };
  return buildPrompt({ clientName: client.name, period, meta, funnel, comparison, notes });
};

const kpiRow = (label, current, previous, fmt, higherBetter) => {
  const up = current >= previous;
  const good = higherBetter == null ? null : higherBetter ? up : !up;
  return { label, current: fmt(current), previous: fmt(previous), delta: deltaStr(current, previous), good };
};

export function generateOperationalReport({ client, days, period, notes, author, promptOverride }) {
  const meta = clientMeta(client, days);
  const funnel = clientFunnel(client, days);
  const comparison = { prev1: simulatePrev(meta, 0.88), prev2: simulatePrev(meta, 0.79) };
  const prompt = promptOverride && promptOverride.trim()
    ? promptOverride
    : buildPrompt({ clientName: client.name, period, meta, funnel, comparison, notes });

  const p = 0.88; // fattore mese precedente per le metriche funnel (simulato)
  const conversionRate = meta.leads ? ((funnel.appointmentsBooked / meta.leads) * 100).toFixed(1) : "0";
  const cplDown = meta.cpl <= comparison.prev1.cpl;

  const kpi = [
    kpiRow("Lead generati", meta.leads, comparison.prev1.leads, num, true),
    kpiRow("CPL", meta.cpl, comparison.prev1.cpl, eur, false),
    kpiRow("Spesa", meta.spesa, comparison.prev1.spesa, eur, null),
    kpiRow("Impression", meta.impression, Math.round(meta.impression * p), num, true),
    kpiRow("Reach", meta.reach, Math.round(meta.reach * p), num, true),
    kpiRow("Appuntamenti fissati", funnel.appointmentsBooked, Math.round(funnel.appointmentsBooked * p), num, true),
    kpiRow("Programmi venduti", funnel.programsSold, Math.round(funnel.programsSold * 0.85), num, true),
    kpiRow("No Show", funnel.noShow, Math.round(funnel.noShow * 0.92), num, false),
    kpiRow("Numeri errati", funnel.wrongNumbers, Math.round(funnel.wrongNumbers * 0.95), num, false),
    kpiRow("Fuori zona", funnel.outsideServiceArea, Math.round(funnel.outsideServiceArea * 0.95), num, false),
    kpiRow("Non interessati", funnel.notInterested, Math.round(funnel.notInterested * 0.93), num, false),
    kpiRow("Contatti gestiti", funnel.managedContacts, Math.round(funnel.managedContacts * p), num, true),
    kpiRow("Contatti da gestire", funnel.contactsToManage, Math.round(funnel.contactsToManage * 1.08), num, false),
  ];

  const introduction =
    `Nel periodo ${period} le campagne Meta di ${client.name} hanno generato ${num(meta.leads)} lead ` +
    `con una spesa di ${eur(meta.spesa)} e un CPL medio di ${eur(meta.cpl)} ` +
    `(${deltaStr(meta.cpl, comparison.prev1.cpl)} rispetto al mese precedente). ` +
    `Il tasso di conversione lead → appuntamento si attesta al ${conversionRate}%, ` +
    `con ${num(funnel.appointmentsBooked)} appuntamenti fissati e ${num(funnel.programsSold)} programmi venduti.`;

  const analysis = [
    `Sul fronte advertising, il volume di lead è ${meta.leads >= comparison.prev1.leads ? "in crescita" : "in calo"} ` +
      `(${deltaStr(meta.leads, comparison.prev1.leads)} vs mese precedente) e il costo per lead è ${cplDown ? "in miglioramento" : "in aumento"}. ` +
      `Reach e impression confermano una copertura ${meta.reach > 0 ? "stabile" : "limitata"} del pubblico target.`,
    `Lato operativo, dal Google Sheet del cliente risultano ${num(funnel.managedContacts)} contatti già gestiti e ` +
      `${num(funnel.contactsToManage)} ancora in coda. ` +
      `Le perdite di funnel si concentrano su ${num(funnel.notInterested)} non interessati, ` +
      `${num(funnel.wrongNumbers)} numeri errati e ${num(funnel.outsideServiceArea)} contatti fuori zona di servizio.`,
    `Note dell'operatore: ${notes && notes.trim() ? notes.trim() : "nessuna modifica significativa segnalata in questo periodo."}`,
  ];

  const nextSteps = [
    cplDown
      ? "Scalare il budget sulle campagne con CPL più basso per consolidare il trend positivo."
      : "Rivedere creatività e targeting delle campagne con CPL più alto prima di aumentare il budget.",
    funnel.contactsToManage > funnel.managedContacts
      ? `Smaltire il backlog di ${num(funnel.contactsToManage)} contatti da gestire accelerando il primo contatto.`
      : "Mantenere l'attuale ritmo di gestione contatti: i tempi di follow-up sono nella norma.",
    funnel.noShow > funnel.programsSold
      ? "Introdurre promemoria automatici pre-appuntamento per ridurre i No Show."
      : "Consolidare il processo di conferma appuntamenti, che sta funzionando bene.",
  ];

  const data = {
    title: `Report Operativo — ${client.name}`,
    period,
    introduction,
    kpi,
    analysis,
    nextSteps,
  };

  return { data, prompt };
}
